import { type AxiosPromise } from "axios";
import { ButtonPrimary } from "components/Atoms/Button";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import { HtmlEditor } from "components/ValidatedFields/HtmlEditor";
import React from "react";
import { Input, Message } from "semantic-ui-react";
import { newLineToBr } from "utilities";
import { routes } from "utilities/routing/generatedRoutes";
import { LabelledRow } from "../../components";

interface Props {
  config: server.dto.LinkedNotePanelConfig
  existingNote?: server.dto.Note
  mainArea: server.dto.MainAreas
  onFinishedSaving: () => void

  noteState: NoteState
  onNoteStateChanged: (noteState: Partial<NoteState>) => void
}

export interface NoteState {
  charactersRemaining: number

  visibleToExternal: boolean
  subject: string
  body: string
  subjects: string[]
  isSaving: boolean
  noSubjectError: boolean
  bodyTooLongError: boolean
  noteError: string // We can use this in the future
}

const NoteCreateModal = ({ existingNote, mainArea, config, onFinishedSaving, noteState, onNoteStateChanged }: Props) => {
  const {
    visibleToExternal,
    subject,
    body,
    subjects,
    noSubjectError,
    bodyTooLongError,
    isSaving,
    noteError
  } = noteState;

  const isNewNote = !existingNote;

  React.useEffect(() => {
    const fetchSubject = async () => {
      const resp = await window.CoreApi.FavoriteSubject.ListSubjects();
      if (resp.data.hasErrors) {
        return;
      }
      onNoteStateChanged({ subjects: resp.data.data.map(s => s.subjectValue) });
    }
    fetchSubject();
  }, []);

  const onSave = async () => {
    const hasNoSubject = !subject || subject.trim().length === 0;
    const bodyIsTooLong = noteState.charactersRemaining < 0;
    if (hasNoSubject || bodyIsTooLong) {
      onNoteStateChanged({
        noSubjectError: hasNoSubject,
        bodyTooLongError: bodyIsTooLong
      });
      return;
    }

    onNoteStateChanged({ isSaving: true });

    let promise: AxiosPromise<any>;

    if (isNewNote) {
      promise = window.CoreApi.Note.CreateNote({
        mainArea,
        parentId: config.itemId,
        title: subject,
        body: body ?? "",
        visibleToExternal
      });
    } else {
      promise = window.CoreApi.Note.UpdateNote({
        noteId: existingNote.noteId,
        title: subject,
        body,
        visibleToExternal
      });
    }

    await promise;

    onFinishedSaving();
  };

  const onSubjectChange = (newSubject: string) => {
    onNoteStateChanged({ subject: newSubject, noSubjectError: false });
  };

  return (<>
    <div className="grow1 flex-column gap scrollbox-auto" style={{ minHeight: 0 }}>
      <table className="s1_table bordergrid gray ">
        <tbody>
          {config.showVisibleToExternalCheckbox && <LabelledRow
            label={RESX.GeneralLabels.Visible}
            bigPadding>
            <Checkbox
              checked={visibleToExternal}
              onChange={() => { onNoteStateChanged({ visibleToExternal: !visibleToExternal }); }}
              label={RESX.GeneralLabels.VisibleToExternalUsers}
            />
          </LabelledRow>}

          <LabelledRow label={RESX.Message.Subject}>
            <div className="flex">
              <div className="grow1 subjects padding-small">
                {subjects.map((subject, idx) =>
                  <a onClick={() => { onSubjectChange(subject); }} key={subject + idx}>
                    {subject}
                  </a>
                )}
              </div>
              {config.displayFavoriteSubjects && <div className="padding-small">
                <a href={routes.personal_FavoriteSubject_List().absolute} className="icon-cog"></a>
              </div>}
              <div className="padding-small" style={{
                fontSize: "large",
                color: bodyTooLongError ? "var(--error-color)" : "var(--text-color-light)",
              }}>
                {noteState.charactersRemaining}
              </div>
            </div>
            <div>
              <Input
                value={subject}
                onChange={(_, { value }) => { onSubjectChange(value); }}
                fluid
                error={noSubjectError}
                maxLength={serverReference.constants.note.maxTitleLength}
              />
            </div>
          </LabelledRow>
        </tbody>
      </table>

      <HtmlEditor
        toolbar="Normal"
        content={body}
        maxContentLength={serverReference.constants.note.maxBodyLength}
        onChange={(newContent, charactersRemaining) => onNoteStateChanged({
          body: newContent,
          bodyTooLongError: false,
          charactersRemaining
        })}
      />
    </div>

    <div className="flex gap">
      <div className="grow1">
        {noteError && <Message color="red">{newLineToBr(noteError)}</Message>}
      </div>
      <div>
        <ButtonPrimary
          loading={isSaving}
          disabled={isSaving}
          onClick={onSave}
          label={RESX.Buttons.btnSave} />
      </div>
    </div>
  </>
  );
};

export default NoteCreateModal;